import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  CalendarCheck,
  Images,
  MessageSquare,
  FolderTree,
  Package,
  HelpCircle,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  Command,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useConfirm, ConfirmDialog } from "./ConfirmDialog";
import CommandPalette from "./CommandPalette";

const MENU_ITEMS = [
  {
    name: "Dashboard",
    path: "/admin",
    icon: LayoutDashboard,
  },
  {
    name: "Booking",
    path: "/admin/bookings",
    icon: CalendarCheck,
  },
  {
    name: "Gallery",
    path: "/admin/gallery",
    icon: Images,
  },
  {
    name: "Testimoni",
    path: "/admin/testimonials",
    icon: MessageSquare,
  },
  {
    name: "Kategori",
    path: "/admin/categories",
    icon: FolderTree,
  },
  {
    name: "Layanan",
    path: "/admin/packages",
    icon: Package,
  },
  {
    name: "FAQ",
    path: "/admin/faq",
    icon: HelpCircle,
  },
  {
    name: "Pengaturan",
    path: "/admin/settings",
    icon: Settings,
  },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Confirmation dialog hook
  const { confirm, ConfirmDialog } = useConfirm();

  // Header dropdown state
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Command Palette state
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Auto-detect screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
        setMobileMenuOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu) {
        const userMenu = document.getElementById("user-menu-container");
        if (userMenu && !userMenu.contains(event.target)) {
          setShowUserMenu(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  // Keyboard shortcut for Command Palette (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Open Command Palette with Ctrl/Cmd + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for logout event from Command Palette
  useEffect(() => {
    const handleLogoutEvent = () => {
      handleLogout();
    };

    document.addEventListener('admin-logout', handleLogoutEvent);
    return () => document.removeEventListener('admin-logout', handleLogoutEvent);
  }, []);

  const handleLogout = async () => {
    const confirmed = await confirm(
      "Apakah Anda yakin ingin keluar dari akun admin?",
      {
        title: "Konfirmasi Logout",
        type: "danger",
        confirmText: "Ya, Keluar",
        cancelText: "Batal",
      }
    );

    if (!confirmed) return;

    try {
      await logout();
      navigate("/admin");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleMenuClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-screen bg-[#0A0A0A] border-r border-[#D4AF7A]/20
          transition-all duration-300
          ${sidebarOpen || mobileMenuOpen ? "w-64" : "w-[70px]"}
          ${mobileMenuOpen ? "translate-x-0 w-64" : "-translate-x-full"}
          md:translate-x-0 md:${sidebarOpen || mobileMenuOpen ? "w-64" : "w-[70px]"}
        `}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-[#D4AF7A]/20">
          {(sidebarOpen || mobileMenuOpen) && (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C9A882] to-[#D4AF7A] rounded-lg flex items-center justify-center">
                <span className="text-sm font-bold text-black">PH</span>
              </div>
              <span className="text-lg font-bold text-[#F7F5F2]">
                Putri Henna
              </span>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="hidden md:flex p-1.5 rounded-lg hover:bg-white/10 transition-colors text-[#F7F5F2]"
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="px-3 space-y-1">
            {MENU_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const showLabel = sidebarOpen || mobileMenuOpen;

              return (
                <li key={item.path}>
                  <button
                    onClick={() => handleMenuClick(item.path)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-200
                      ${
                        isActive
                          ? "bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] text-black"
                          : "text-[#F7F5F2]/70 hover:bg-white/5 hover:text-[#F7F5F2]"
                      }
                    `}
                  >
                    <Icon
                      className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-black" : ""}`}
                    />
                    {showLabel && (
                      <span className="font-medium truncate">{item.name}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-[#D4AF7A]/20">
          {/* User Profile Card */}
          {(sidebarOpen || mobileMenuOpen) && (
            <div className="mb-4 p-3 rounded-xl bg-gradient-to-br from-[#D4AF7A]/10 to-transparent border border-[#D4AF7A]/20">
              {/* User Info */}
              <div className="flex items-center gap-3 mb-3">
                {/* Avatar/Initials */}
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A882] to-[#D4AF7A] flex items-center justify-center shadow-lg shadow-[#D4AF7A]/20">
                  <span className="text-sm font-bold text-black">
                    {user?.displayName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>

                {/* Name & Role */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#F7F5F2] truncate">
                    {user?.displayName || user?.email?.split('@')[0] || "Admin"}
                  </p>
                  <p className="text-xs text-[#D4AF7A] font-medium">Administrator</p>
                </div>
              </div>

              {/* User Stats - Optional */}
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-[#D4AF7A]/10">
                <div className="text-center">
                  <p className="text-lg font-bold text-[#D4AF7A]">Active</p>
                  <p className="text-xs text-[#F7F5F2]/60">Status</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-[#D4AF7A]">Admin</p>
                  <p className="text-xs text-[#F7F5F2]/60">Role</p>
                </div>
              </div>
            </div>
          )}

          {/* Logout Button - Separated & Styled */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-all duration-200 group"
          >
            <LogOut className="w-4 h-4 group-hover:rotate-[-10deg] transition-transform duration-200" />
            {(sidebarOpen || mobileMenuOpen) && (
              <span className="font-medium text-sm">Keluar</span>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`
          transition-all duration-300 min-h-screen
          ${sidebarOpen ? "md:ml-64" : "md:ml-[70px]"}
        `}
      >
        {/* Top Bar */}
        <header
          className={`
          fixed top-0 right-0 z-30 h-16 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#D4AF7A]/20
          transition-all duration-300
          ${sidebarOpen ? "md:left-64" : "md:left-[70px]"}
          left-0
        `}
        >
          <div className="flex items-center justify-between h-full px-4 sm:px-6">
            {/* Left Side - Mobile Menu & Page Title */}
            <div className="flex items-center gap-4">
              {/* Hamburger Menu (Mobile) */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-[#F7F5F2]"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>

              {/* Page Title */}
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-[#F7F5F2]">
                  {MENU_ITEMS.find((item) => item.path === location.pathname)
                    ?.name || "Admin"}
                </h1>
              </div>
            </div>

            {/* Right Side - Command Palette Trigger & User Profile */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Command Palette Trigger - Desktop Only */}
              <button
                onClick={() => setShowCommandPalette(true)}
                className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-[#D4AF7A]/20 hover:bg-white/10 hover:border-[#D4AF7A]/40 transition-all text-[#F7F5F2]/70 hover:text-[#F7F5F2] group"
              >
                <Search className="w-4 h-4 group-hover:text-[#D4AF7A] transition-colors" />
                <span className="text-sm">Search</span>
                <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 bg-white/10 rounded text-[10px] text-[#F7F5F2]/50 group-hover:bg-white/20 transition-colors">
                  <span>⌘</span>
                  <span>K</span>
                </kbd>
              </button>

              {/* Command Palette Trigger - Mobile Only */}
              <button
                onClick={() => setShowCommandPalette(true)}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors text-[#F7F5F2]"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* User Profile Dropdown */}
              <div className="relative" id="user-menu-container">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors group"
                >
                  {/* Avatar/Initials */}
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C9A882] to-[#D4AF7A] flex items-center justify-center shadow-lg shadow-[#D4AF7A]/20">
                    <span className="text-xs font-bold text-black">
                      {user?.displayName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>

                  {/* User Info - Hidden on Mobile */}
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-semibold text-[#F7F5F2] truncate max-w-[120px]">
                      {user?.displayName || user?.email?.split('@')[0] || "Admin"}
                    </p>
                    <p className="text-xs text-[#D4AF7A]">Administrator</p>
                  </div>

                  {/* Chevron Icon - Hidden on Mobile */}
                  <ChevronDown className={`hidden lg:block w-4 h-4 text-[#F7F5F2]/60 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#0A0A0A] border border-[#D4AF7A]/30 rounded-xl shadow-2xl shadow-black/50 overflow-hidden z-50">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-[#D4AF7A]/20 bg-gradient-to-r from-[#D4AF7A]/10 to-transparent">
                      <p className="text-sm font-semibold text-[#F7F5F2]">
                        {user?.displayName || user?.email || "Admin"}
                      </p>
                      <p className="text-xs text-[#D4AF7A]">
                        {user?.email || "admin@putrihenna.com"}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      <button
                        onClick={() => {
                          navigate("/admin/settings");
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#F7F5F2]/80 hover:bg-white/10 hover:text-[#F7F5F2] transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Pengaturan
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Keluar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Command Palette Modal */}
        <CommandPalette
          open={showCommandPalette}
          onClose={() => setShowCommandPalette(false)}
        />

        {/* Content Area */}
        <main className="p-4 mt-16 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
